import { Platform, Alert } from 'react-native';
import RNFS from 'react-native-fs';
import Mailer from 'react-native-mail';

import {
  isFunction as _isFunction,
  remove as _remove,
  findIndex as _findIndex,
  has as _has,
  forEach as _forEach,
} from 'lodash';

import { InvalidListenerException, InvalidMethodInvocationException } from './Exceptions';

const logFilePath =
  Platform.OS === 'android'
    ? RNFS.ExternalDirectoryPath + '/log.txt'
    : RNFS.DocumentDirectoryPath + '/log.txt';

class LoggerManager {
  constructor() {
    this.__listeners = [];
    this.__instance = console;
    this.__tagsWhiteList = [];
    this.__tagsBlackList = [];
    this.__useInDevOnly = true;
    this.__logFileCreated = false;
  }

  configure = (
    instance = console,
    tagsWhiteList = [],
    tagsBlackList = [],
    useInDevOnly = true,
    logInFile = false,
  ) => {
    this.__instance = instance;
    this.__tagsWhiteList = tagsWhiteList;
    this.__tagsBlackList = tagsBlackList;
    this.__useInDevOnly = useInDevOnly;
    if (logInFile) {
      this.addListener(this.logInFile);
    } else {
      this.removeListener(this.logInFile);
      this.deleteFile();
    }
  };

  addListener = callback => {
    if (!_isFunction(callback)) {
      throw new InvalidListenerException(
        `Expected a function to be passed instead ${typeof callback} was received.`,
      );
    }

    this.__listeners.push(callback);
  };

  removeListener = callback => {
    _remove(this.__listeners, cb => callback === cb);
  };

  log = (tag, ...args) => {
    if (this.__shouldExecute(tag) && (!this.__useInDevOnly || (this.__useInDevOnly && __DEV__))) {
      if (_has(this.__instance, 'log') && _isFunction(this.__instance.log)) {
        this.__instance.log(tag, ...args);
      } else {
        throw new InvalidMethodInvocationException(
          'No such method in the provided instance, Couldn\'t find method "log".',
        );
      }
    }

    this.__invokeListeners('log', tag, ...args);
  };

  warn = (tag, ...args) => {
    if (this.__shouldExecute(tag) && (!this.__useInDevOnly || (this.__useInDevOnly && __DEV__))) {
      if (_has(this.__instance, 'warn') && _isFunction(this.__instance.warn)) {
        this.__instance.warn(tag, ...args);
      } else {
        throw new InvalidMethodInvocationException(
          'No such method in the provided instance, Couldn\'t find method "warn".',
        );
      }
    }

    this.__invokeListeners('warn', tag, ...args);
  };

  error = (tag, ...args) => {
    if (this.__shouldExecute(tag) && (!this.__useInDevOnly || (this.__useInDevOnly && __DEV__))) {
      if (_has(this.__instance, 'error') && _isFunction(this.__instance.error)) {
        this.__instance.error(tag, ...args);
      } else {
        throw new InvalidMethodInvocationException(
          'No such method in the provided instance, Couldn\'t find method "error".',
        );
      }
    }

    this.__invokeListeners('error', tag, ...args);
  };

  debug = (tag, ...args) => {
    if (this.__shouldExecute(tag) && (!this.__useInDevOnly || (this.__useInDevOnly && __DEV__))) {
      if (_has(this.__instance, 'debug') && _isFunction(this.__instance.debug)) {
        this.__instance.debug(tag, ...args);
      } else {
        throw new InvalidMethodInvocationException(
          'No such method in the provided instance, Couldn\'t find method "debug".',
        );
      }
    }

    this.__invokeListeners('debug', tag, ...args);
  };

  info = (tag, ...args) => {
    if (this.__shouldExecute(tag) && (!this.__useInDevOnly || (this.__useInDevOnly && __DEV__))) {
      if (_has(this.__instance, 'info') && _isFunction(this.__instance.info)) {
        this.__instance.info(tag, ...args);
      } else {
        throw new InvalidMethodInvocationException(
          'No such method in the provided instance, Couldn\'t find method "info".',
        );
      }
    }

    this.__invokeListeners('info', tag, ...args);
  };

  logInFile = async (type, tag, ...log) => {
    if (!this.__logFileCreated) {
      await this.__createFile();
    }
    RNFS.write(logFilePath, `-${type}: Tag: ${tag}, ${log.join(', ')}\n`, undefined, 'utf8');
  };

  shareLogFile = () => {
    if (this.__logFileCreated) {
      Mailer.mail(
        {
          attachments: [
            {
              name: 'log.txt',
              path: logFilePath,
              type: 'doc',
            },
          ],
        },
        (error, event) => {
          if (error) {
            Alert.alert(error);
          }
        },
      );
    }
  };

  deleteFile = async () => {
    RNFS.exists(logFilePath)
      .then(() => {
        RNFS.unlink(logFilePath)
          .then(() => {
            this.__logFileCreated = false;
            return Promise.resolve('File deleted');
          })
          .catch(error => {
            return Promise.reject(error);
          });
      })
      .catch(() => {
        return Promise.resolve('File not exists');
      });
  };

  __shouldExecute = tag => {
    return (
      (this.__tagsWhiteList.length === 0 && this.__tagsBlackList.length === 0) ||
      (this.__tagsBlackList.length > 0 &&
        _findIndex(this.__tagsBlackList, t => t === tag) === -1) ||
      (this.__tagsWhiteList.length > 0 && _findIndex(this.__tagsWhiteList, t => t === tag) !== -1)
    );
  };

  __invokeListeners = (...args) => {
    _forEach(this.__listeners, listener => {
      listener(...args);
    });
  };

  __createFile = () => {
    RNFS.writeFile(logFilePath, '', 'utf8')
      .then(() => {
        this.__logFileCreated = true;
        return Promise.resolve('file created');
      })
      .catch(error => {
        return Promise.reject(error);
      });
  };
}

export default LoggerManager;
